import React, { memo, useCallback, useMemo, useState } from 'react';
import { BlockStack, InlineStack, Text, Thumbnail, Tooltip, Button, Tag } from '@shopify/polaris';
import { DuplicateIcon, CheckIcon } from '@shopify/polaris-icons';
import { CombinedGenerationConfiguration, MediaAsset } from '@/types/media';
import { StudioVideo } from '@/features/ai-video-creator/stores/studioVideoStore';

const ReferenceImagesSection = memo(({ sources }: { sources: string[] }) => {
  if (!sources.length) return null;

  return (
    <BlockStack gap="300">
      <Text variant="headingMd" as="h3">
        Reference Image{sources.length > 1 ? 's' : ''}
      </Text>
      <InlineStack gap="300" wrap>
        {sources.map((src, index) => (
          <Thumbnail key={`${src}-${index}`} source={src} alt={`Reference Image ${index + 1}`} size="large" />
        ))}
      </InlineStack>
    </BlockStack>
  );
});
ReferenceImagesSection.displayName = 'ReferenceImagesSection';

const PromptSection = memo(({ prompt }: { prompt: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyPrompt = useCallback(() => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [prompt]);

  if (!prompt) return null;
  return (
    <BlockStack gap="300">
      <InlineStack align="start" blockAlign="center" gap="200">
        <Text variant="headingMd" as="h3">
          Prompt
        </Text>
        <Tooltip content={copied ? 'Copied!' : 'Copy prompt'} dismissOnMouseOut>
          <Button
            variant="plain"
            icon={copied ? CheckIcon : DuplicateIcon}
            onClick={handleCopyPrompt}
            accessibilityLabel="Copy prompt"
          />
        </Tooltip>
      </InlineStack>
      <Text variant="bodyMd" as="p" tone="subdued">
        {prompt}
      </Text>
    </BlockStack>
  );
});
PromptSection.displayName = 'PromptSection';

const DetailsSection = memo(
  ({
    provider,
    model,
    mode,
    aspectRatio,
    toolName,
  }: Pick<CombinedGenerationConfiguration, 'provider' | 'model' | 'mode' | 'aspectRatio'> & { toolName?: string }) => {
    const formattedModel = useMemo(() => {
      if (!model) return '';
      return model
        .replace(/-/g, ' ')
        .replace(/v(\d)/, '$1')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }, [model]);

    if (!provider && !model && !mode && !aspectRatio && !toolName) return null;

    return (
      <BlockStack gap="300">
        <Text variant="headingMd" as="h3">
          Details
        </Text>
        <InlineStack gap="200" wrap>
          {provider && <Tag>{provider.charAt(0).toUpperCase() + provider.slice(1)}</Tag>}
          {formattedModel && <Tag>{formattedModel}</Tag>}
          {mode && <Tag>{mode === 'pro' ? 'Professional Mode' : mode.charAt(0).toUpperCase() + mode.slice(1)}</Tag>}
          {aspectRatio && <Tag>{`Ratio: ${aspectRatio}`}</Tag>}
          {toolName && <Tag>{toolName}</Tag>}
        </InlineStack>
      </BlockStack>
    );
  }
);
DetailsSection.displayName = 'DetailsSection';

const ProductSection = memo(({ productImage, productTitle }: { productImage: string; productTitle?: string }) => {
  return (
    <BlockStack gap="300">
      <Text variant="headingMd" as="h3">
        Product
      </Text>
      {productTitle && (
        <Text variant="bodyMd" as="p">
          {productTitle}
        </Text>
      )}
      <Thumbnail source={productImage} alt="Product Image" size="large" />
    </BlockStack>
  );
});
ProductSection.displayName = 'ProductSection';

const GenerationSettingsPanel = ({
  mediaAsset,
  assetName,
  studioVideo,
}: {
  mediaAsset: MediaAsset | null;
  assetName?: string;
  studioVideo: StudioVideo | null;
}) => {
  const config = useMemo(
    () =>
      mediaAsset && 'generationConfiguration' in mediaAsset
        ? (mediaAsset.generationConfiguration as CombinedGenerationConfiguration)
        : null,
    [mediaAsset]
  );

  const toolName = useMemo(() => {
    return mediaAsset?.createdWith?.toolName || '';
  }, [mediaAsset]);

  const referenceImageSources = useMemo(() => {
    const images: string[] = [];

    if (config) {
      const configImages = [
        config.image,
        config.customerImage,
        config.productImage,
        config.backgroundReferenceImage,
      ].filter(Boolean) as string[];
      images.push(...configImages);
    }

    if (studioVideo?.animateImages) {
      const animateImageSources = studioVideo.animateImages
        .flatMap((img) => {
          if (typeof img === 'string') return [img];
          return Object.values(img.sources).filter(Boolean) as string[];
        })
        .filter(Boolean);
      images.push(...animateImageSources);
    }

    return Array.from(new Set(images));
  }, [config, studioVideo]);

  const displayPrompt = useMemo(() => {
    if (config) {
      return config.videoPrompt || config.imagePrompt || config.backgroundPrompt || '';
    }
    if (studioVideo) {
      return studioVideo.videoPrompt || '';
    }
    return '';
  }, [config, studioVideo]);

  const productImage = useMemo(() => {
    if (studioVideo?.product?.imageUrl) {
      return studioVideo.product.imageUrl;
    }
    return undefined;
  }, [studioVideo]);

  const productTitle = useMemo(() => {
    if (studioVideo?.product?.title) {
      return studioVideo.product.title;
    }
    return undefined;
  }, [studioVideo]);

  return (
    <div className="bg-surface h-full min-h-0 p-5 md:border-e" style={{ height: '100%' }}>
      <div className="h-full overflow-y-auto">
        <BlockStack gap="600">
          <Text variant="headingLg" as="h2">
            {assetName || 'Generation Settings'}
          </Text>
          {productImage && <ProductSection productImage={productImage} productTitle={productTitle} />}
          <ReferenceImagesSection sources={referenceImageSources} />
          <PromptSection prompt={displayPrompt} />
          {config && (
            <DetailsSection
              provider={config.provider}
              model={config.model}
              mode={config.mode}
              aspectRatio={config.aspectRatio}
              toolName={toolName}
            />
          )}
        </BlockStack>
      </div>
    </div>
  );
};

export default memo(GenerationSettingsPanel);
